import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllGrades,
  getGradesForStudent,
  getGradesForAssessment,
  createGrade,
  updateGrade,
  deleteGrade,
  getGradeById  // Import the function here
} from '../services/gradeService';
import Notification from '../components/Common/Notification';

export const GradeContext = createContext();

export const GradeProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    if (!user && !storedUser) {
      console.error('User or token not available');
      return {};
    }

    const token = user?.token || JSON.parse(storedUser).token;
    return { Authorization: `Bearer ${token}` };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllGrades = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllGrades(authHeaders);
      setGrades(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching grades.';
      showNotification(errorMessage, true);
    }
  };

  const fetchGradesForStudent = async (studentId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradesForStudent(studentId, authHeaders);
      setGrades(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grades for student with ID ${studentId}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchGradesForAssessment = async (assessmentId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradesForAssessment(assessmentId, authHeaders);
      setGrades(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grades for assessment with ID ${assessmentId}.`;
      showNotification(errorMessage, true);
    }
  };

  const addGrade = async (gradeData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newGrade = await createGrade(gradeData, authHeaders);
      setGrades(prevGrades => [...prevGrades, newGrade]);
      showNotification('Grade added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding grade.';
      showNotification(errorMessage, true);
    }
  };

  const editGrade = async (id, gradeData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedGrade = await updateGrade(id, gradeData, authHeaders);
      setGrades(prevGrades =>
        prevGrades.map(grade => (grade.id === id ? updatedGrade : grade))
      );
      showNotification('Grade updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating grade.';
      showNotification(errorMessage, true);
    }
  };

  const removeGrade = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteGrade(id, authHeaders);
      setGrades(prevGrades => prevGrades.filter(grade => grade.id !== id));
      showNotification('Grade removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing grade.';
      showNotification(errorMessage, true);
    }
  };

  const fetchGradeById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const grade = await getGradeById(id, authHeaders);
      setSelectedGrade(grade);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grade with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllGrades();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <GradeContext.Provider
      value={{
        grades,
        selectedGrade,
        fetchAllGrades,
        fetchGradesForStudent,
        fetchGradesForAssessment,
        addGrade,
        editGrade,
        removeGrade,
        fetchGradeById,  // Add the function here
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </GradeContext.Provider>
  );
};
